import { Grid, Box, Heading, SimpleGrid, Text, Flex, Icon } from '@chakra-ui/react'
import { FormProvider, useForm } from 'react-hook-form'
import { VerifyFormButton } from '../atoms/VerifyFormButton'
import { VerifyFormTextarea } from '../atoms/VerifyFormTextarea'
import { EmptyPlaceholder } from '../molecules/EmptyPlaceholder'
import { VerifyFormTitle } from '../molecules/VerifyFormTitle'
import { useEffect, useState } from 'react'
import { VerifyFormInput } from '../atoms/VerifyFormInput'
import Image from 'next/image'
import TicketIcon from '@/public/assets/icons/ticket.svg'
import { useLootboxContext } from '@/context/LootboxProvider'
import { convertUInt32ToFloats, getUint32 } from '../pkg/rng_v2'
import { sha256 } from '@noble/hashes/sha256'
import { LootboxV2, RewardV2 } from '../../Lootbox/templates/transformLootboxRewards'
import * as yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import { Section } from '../utils'
import { VerifyFormSelect } from '../molecules/VerifyFormSelect'
import { useTranslation } from 'next-i18next/pages'

type LootboxVerifyResultState = {
  rewardResult: (RewardV2 & { ticket: number })[]
  lootbox: LootboxV2
}

const schema = yup.object({
  signature: yup
    .string()
    .required()
    .label('Signature')
    .test('is-hex', '${path} is not hex', (value) => /^(0x)?[0-9a-fA-F]+$/.test(value || '')),
  gameId: yup
    .number()
    .transform((value) => (isNaN(value) ? undefined : value))
    .required()
    .label('Game id')
    .min(1),
  box: yup.string().required().label('Box'),
  quantity: yup
    .number()
    .transform((value) => (isNaN(value) ? undefined : value))
    .required()
    .min(1)
    .max(5)
    .label('Quantity'),
})

export const LootboxVerify = () => {
  const form = useForm({
    resolver: yupResolver(schema),
  })
  const { t } = useTranslation()

  const [state, setState] = useState<LootboxVerifyResultState | undefined>()

  const { getLootboxCollection } = useLootboxContext()

  useEffect(() => {
    const { unsubscribe } = form.watch(() => {
      setState(undefined)
    })
    return () => unsubscribe()
  }, [form.watch])

  const handleClick = async () => {
    await form.trigger()

    if (!form.formState.isValid) {
      setState(undefined)
      return
    }

    const signature = form.getValues('signature').replace('0x', '')

    const box = form.getValues('box')
    let lootboxConfig = getLootboxCollection(box)

    if (!lootboxConfig) {
      return
    }

    const rewards = Object.values(lootboxConfig.rewards) as RewardV2[]

    const totalWeight = rewards.reduce((acc, cur) => acc + cur.weight, 0)

    const randomness = Buffer.from((await sha256(signature)) as any).toString('hex')

    const gameId = form.getValues('gameId')
    const lootbox = lootboxConfig.id

    const quantity = form.getValues('quantity')

    const result = getUint32({
      serverKey: Buffer.from(randomness, 'hex'),
      userSeed: Buffer.from(`${gameId}-${lootbox}`, 'utf-8'),
      count: quantity,
    })
      .map(convertUInt32ToFloats)
      .map((x: number) => x * totalWeight)
      .map(Math.floor) as number[]

    const rewardResult = result.map((ticket: number) => {
      let currentWeight = 0
      for (let reward of rewards) {
        currentWeight += reward.weight
        if (ticket < currentWeight) {
          return { ...reward, ticket: ticket }
        }
      }
      return { ...rewards.at(-1)!, ticket: ticket }
    })

    setState({
      rewardResult: rewardResult as any,
      lootbox: lootboxConfig as any,
    })
  }

  return (
    <Grid gap={5} gridTemplateColumns={{ base: '1fr', lg: '260px auto' }}>
      <LootboxVerifyForm handleClick={handleClick} form={form} />
      <Box p={5} minH={'375px'} rounded={'24px'} bg={'ninja.600'}>
        <Heading fontSize={'xl'}>{t('verify.result')}</Heading>
        {!state ? <EmptyPlaceholder game={Section.LOOTBOX} /> : <LootboxVerifyResult {...state} />}
      </Box>
    </Grid>
  )
}

const LootboxVerifyForm = ({ handleClick, form }: { handleClick: () => void; form: any }) => {
  const { getLootboxCollection, getLootboxImagePrefix } = useLootboxContext()
  const { t } = useTranslation()

  return (
    <Box p={5} rounded={'24px'} bg={'ninja.600'}>
      <FormProvider {...form}>
        <VerifyFormTitle mode="drng" />
        <VerifyFormTextarea label={t('verify.signature')} placeholder={t('verify.enter-signature')} name="signature" />
        <VerifyFormInput
          label={t('verify.quantity')}
          placeholder={t('verify.enter-quantity')}
          name={'quantity'}
          type={'number'}
        />
        <VerifyFormInput
          label={t('verify.game-id')}
          placeholder={t('verify.enter-game-id')}
          name={'gameId'}
          type={'number'}
        />
        <VerifyFormSelect
          placeholder={t('verify.choose-box')}
          label={t('verify.box')}
          name="box"
          options={[
            {
              label: 'Sai Box',
              value: 'sai-box',
              image: getLootboxImagePrefix('sai-box') + getLootboxCollection('sai-box').image.src,
            },
            {
              label: 'Shuriken Box',
              value: 'shuriken-box',
              image: getLootboxImagePrefix('shuriken-box') + getLootboxCollection('shuriken-box').image.src,
            },
            {
              label: 'Katana Box',
              value: 'katana-box',
              image: getLootboxImagePrefix('katana-box') + getLootboxCollection('katana-box').image.src,
            },
          ]}
        />
        <VerifyFormButton onClick={handleClick} />
      </FormProvider>
    </Box>
  )
}

const LootboxVerifyResult = (props: LootboxVerifyResultState) => {
  const { getLootboxImagePrefix } = useLootboxContext()
  const { t } = useTranslation()

  return (
    <>
      <SimpleGrid my={6} gap={3} columns={{ base: 1, sm: 1, md: 3, lg: 2, xl: 3, '2xl': 5 }}>
        {props.rewardResult.map((reward, index) => (
          <Box key={index} fontSize={'xs'} fontWeight={700} p={5} rounded={'20px'} bg={'ninja.700'}>
            <Text lineHeight={1} color={'ninja.300'}>
              {t('verify.loot-number', { number: index + 1 })}
            </Text>
            <Text lineHeight={1.1} mb={3}>
              {t('verify.ticket-number', { number: reward.ticket })}
            </Text>
            <Flex alignItems={'center'} gap={2}>
              <Box boxSize={20}>
                <Image
                  width={80}
                  height={80}
                  src={getLootboxImagePrefix(props.lootbox.slug) + '/' + reward.image.inj}
                  alt={''}
                />
              </Box>
              <Box
                fontSize={'xs'}
                fontWeight={700}
                lineHeight={1}
                py={1.5}
                px={2.5}
                border={'1px solid #AC79FF'}
                rounded={'10px'}
              >
                x{reward.multiplier}
              </Box>
            </Flex>
          </Box>
        ))}
      </SimpleGrid>
      <LootboxBoxContents lootbox={props.lootbox} />
    </>
  )
}

const LootboxBoxContents = ({ lootbox }: { lootbox: LootboxV2 }) => {
  const { getLootboxImagePrefix } = useLootboxContext()
  const { t } = useTranslation()

  let cumulativeWeight = 0
  return (
    <>
      <Heading mb={5} fontSize={'xl'}>
        {t('verify.box-contents')}
      </Heading>
      <SimpleGrid gap={3} columns={{ base: 1, sm: 2, md: 3, lg: 2, xl: 3, '2xl': 5 }}>
        {Object.entries(lootbox.rewards).map(([key, reward]) => {
          const previousWeight = cumulativeWeight
          cumulativeWeight += reward.weight
          return (
            <Box key={key} p={2} rounded={'16px'} bg={'rgba(255, 255, 255, 0.01)'}>
              <Flex alignItems={'center'} justifyContent={'center'} gap={2}>
                <Box boxSize={12}>
                  <Image
                    width={48}
                    height={48}
                    src={getLootboxImagePrefix(lootbox.slug) + '/' + reward.image.inj}
                    alt={''}
                  />
                </Box>
                <Box
                  fontSize={'xs'}
                  fontWeight={700}
                  lineHeight={1}
                  py={1.5}
                  px={2.5}
                  border={'1px solid #AC79FF'}
                  rounded={'10px'}
                >
                  x{reward.multiplier}
                </Box>
              </Flex>
              <Flex
                px={2.5}
                py={1.5}
                rounded={'10px'}
                bg={'rgba(255, 255, 255, 0.04);'}
                border={'1px solid rgba(255, 255, 255, 0.04)'}
                alignItems={'center'}
                justifyContent={'space-between'}
                color={'ninja.200'}
              >
                <Icon as={TicketIcon} />
                <Text fontSize={'xs'} fontWeight={700}>
                  {previousWeight}-{previousWeight + reward.weight}
                </Text>
              </Flex>
            </Box>
          )
        })}
      </SimpleGrid>
    </>
  )
}
